import React, { useState, useEffect } from "react";

const ExamSubjectLinkForm = ({ exam, subjects, onSubmit, onClose }) => {
  const [linkedSubjects, setLinkedSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Initialize with currently linked subjects
  useEffect(() => {
    if (exam && subjects) {
      const currentlyLinked = subjects
        .filter(s => s.exams && s.exams.some(e => (e._id || e) === exam._id))
        .map(subject => ({
          subjectId: subject._id,
          subject: subject,
          isLinked: true
        }));
      
      setLinkedSubjects(currentlyLinked);
    }
  }, [exam, subjects]);

  // Filter subjects based on search
  const filteredSubjects = subjects.filter(subject =>
    subject.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subject.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle subject link
  const toggleSubjectLink = (subject) => {
    setLinkedSubjects(prev => {
      const existing = prev.find(s => s.subjectId === subject._id);
      if (existing) {
        // Unlink
        return prev.filter(s => s.subjectId !== subject._id);
      } else {
        // Link
        return [...prev, {
          subjectId: subject._id,
          subject: subject,
          isLinked: false
        }];
      }
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await onSubmit(exam._id, linkedSubjects);
      if (result?.success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h4 className="modal-title">Link Subjects to {exam?.title}</h4>
            <p className="text-13 text-light-1 mt-5">
              Select subjects to link to this exam
            </p>
          </div>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        
        <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit} className="content-form">
            {/* Search */}
            <div className="form-group mb-20">
              <input
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ padding: '10px 15px' }}
              />
            </div>

            {/* Subjects List */}
            <div className="subjects-link-list" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {filteredSubjects.length === 0 ? (
                <div className="text-center py-30 text-13 text-light-2">
                  <i className="icon-search text-30 mb-10"></i>
                  <p>No subjects found</p>
                </div>
              ) : (
                filteredSubjects.map((subject) => {
                  const linkedItem = linkedSubjects.find(s => s.subjectId === subject._id);
                  const isLinked = !!linkedItem;

                  return (
                    <div key={subject._id} className="subject-link-item bg-light-5 rounded-8 p-15 mb-15 border border-light-3">
                      {/* Subject Header */}
                      <div className="d-flex items-center justify-between">
                        <div className="d-flex items-center flex-1">
                          <label className="checkbox-label" style={{ margin: 0, cursor: 'pointer', flex: 1 }}>
                            <input
                              type="checkbox"
                              checked={isLinked}
                              onChange={() => toggleSubjectLink(subject)}
                              className="form-checkbox mr-10"
                            />
                            <div className="d-flex items-center">
                              <div 
                                className="size-35 d-flex justify-center items-center rounded-6 mr-10"
                                style={{backgroundColor: subject.color + '20', color: subject.color}}
                              >
                                <i className={`text-14 ${subject.icon || 'icon-book'}`}></i>
                              </div>
                              <div>
                                <div className="text-14 fw-500">{subject.title}</div>
                                <div className="text-12 text-light-2">{subject.description || 'No description'}</div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="modal-footer mt-20">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting || linkedSubjects.length === 0}
                className="btn-primary bg-purple"
              >
                {submitting ? 'Saving...' : `Link ${linkedSubjects.length} Subject${linkedSubjects.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExamSubjectLinkForm;

